import React from 'react';
import { connect } from 'react-redux';
import PropTypes from 'prop-types';
import Flexbox from 'flexbox-react';
import { css, cx } from 'emotion';
import { createPatch } from 'diff';
import { Diff2Html } from 'diff2html';
import ProcessService from 'services/ProcessService';
import ProcessConverter from 'components/page/process/ProcessConverter';
import AbstractFilteredList from 'components/core/AbstractFilteredList';
import ListItem from 'components/core/ListItem';
import EntityHeader from 'components/core/EntityHeader';
import ToolbarItem from 'components/core/toolbar/ToolbarItem';
import NavigationUtils from 'utils/NavigationUtils';

class RevisionHistory extends React.Component {
    static propTypes = {
        processId: PropTypes.string,
        process: PropTypes.object,
        processRevisions: PropTypes.array,
        processRevisionsById: PropTypes.object,
        originalCopy: PropTypes.object
    };

    constructor(props) {
        super(props);

        this.currentRevisionKey = null;
        this.COMPARE_LABEL = 'Compare with each other';
        this.COMPARE_CURRENT_LABEL = 'Compare with current';

        this.state = {
            showComparison: false,
            compareRevisions: []
        };
    }

    componentWillMount() {
        // Get the process id from the route
        ProcessService.fetchProcessRevisionsIfNeeded(this.props.processId);
    }

    componentWillReceiveProps(nextProps) {
        if (nextProps.processId !== this.props.processId) {
            ProcessService.fetchProcessRevisionsIfNeeded(this.props.processId);
        }
    }

    isContextMenuItemDisabled = (menuItem, triggerName, selectedItems) => {
        let disabled = false;

        if (menuItem === this.COMPARE_CURRENT_LABEL) {
            if (triggerName === this.currentRevisionKey) {
                disabled = true;
            } else if (Object.keys(selectedItems).length > 1) {
                disabled = true;
            }
        }

        if (menuItem === this.COMPARE_LABEL) {
            if (Object.keys(selectedItems).length !== 2) {
                disabled = true;
            }
        }

        return disabled;
    };

    onContextMenuClicked = (e, data) => {
        const processId = this.props.processId;
        switch (data.action) {
            case 'open':
                Object.keys(data.selectedItems).forEach((key, value) => {
                    let revisionNum = key;
                    if (revisionNum === this.currentRevisionKey) {
                        revisionNum = null;
                    }
                    NavigationUtils.gotoProcess(processId, revisionNum, false);
                });
                break;
            case 'compareCurrent':
                //get first selected revision
                let revisionNum = Object.keys(data.selectedItems)[0];
                if (revisionNum !== this.currentRevisionKey) {
                    ProcessService.fetchProcessRevisionIfNeeded(processId, revisionNum);
                } else {
                    revisionNum = 'current';
                }

                //get current
                ProcessService.fetchProcessIfNeeded(processId);

                this.setState({
                    showComparison: true,
                    compareRevisions: [revisionNum, 'current']
                });
                break;
            case 'compare':
                //get first selected revision
                let revisionNum1 = Object.keys(data.selectedItems)[0];
                if (revisionNum1 !== this.currentRevisionKey) {
                    ProcessService.fetchProcessRevisionIfNeeded(processId, revisionNum1);
                } else {
                    revisionNum1 = 'current';
                }

                let revisionNum2 = Object.keys(data.selectedItems)[1];
                if (revisionNum2 !== this.currentRevisionKey) {
                    ProcessService.fetchProcessRevisionIfNeeded(processId, revisionNum2);
                } else {
                    revisionNum2 = 'current';
                }

                if (revisionNum1 === 'current' || revisionNum2 === 'current') {
                    ProcessService.fetchProcessIfNeeded(processId);
                }

                this.setState({
                    showComparison: true,
                    compareRevisions: [revisionNum1, revisionNum2]
                });
                break;
            default:
                break;

        }
    };

    onDoubleClick = (e) => {
        let revisionNumber = e.currentTarget.getAttribute('data-name');
        if (revisionNumber === this.currentRevisionKey) {
            revisionNumber = null;
        }
        NavigationUtils.gotoProcess(this.props.processId, revisionNumber, false);
    };

    onComparisonClose = () => {
        this.setState({ showComparison: false });
    };

    renderCompareRevisions = (oldProcess, newProcess) => {
        // want the display to use old json format
        const oldProcessOldJson = ProcessConverter.newToOldProcess(oldProcess);
        const newProcessOldJson = ProcessConverter.newToOldProcess(newProcess);

        const diffText = createPatch('diff', JSON.stringify(oldProcessOldJson, null, 2), JSON.stringify(newProcessOldJson, null, 2), '', '', { context: 1000 });
        const html = Diff2Html.getPrettyHtml(
            diffText,
            {
                inputFormat: 'diff',
                outputFormat: 'side-by-side',
                matching: 'lines'
            }
        );

        return (
            <Flexbox className="revision-comparison" flexGrow={1}>
                <div dangerouslySetInnerHTML={{ __html: html }} />
            </Flexbox>
        );

    };

    renderComparisonHeader = () => {
        const revIds = this.state.compareRevisions.sort();
        const label = `rev ${revIds[0]}:${revIds[1] === 'current' ? 'current' : `rev ${revIds[1]}`}`;

        return (
            <Flexbox className="comparison-header noshrink">
                <div className="header-box">
                    <span className="rev-label">{label}</span>
                    <ToolbarItem
                        title="Close"
                        className="fa fa-times close"
                        clickHandler={this.onComparisonClose}
                        enabled
                    />
                </div>
            </Flexbox>
        );
    };

    render() {
        let data = {};
        let sortedIds = [];
        if (this.props.processRevisions) {
            data = this.props.processRevisions.reduce((map, obj) => {
                const newMap = Object.assign({}, map);
                newMap[obj.rev_num] = obj;
                return newMap;
            }, {});

            sortedIds = Object.keys(data).sort((a, b) => {
                const intA = parseInt(a, 10);
                const intB = parseInt(b, 10);

                if (intA < intB) {
                    return -1;
                } else if (intA > intB) {
                    return 1;
                }
                return 0;
            });

            //last revision is current revision
            this.currentRevisionKey = sortedIds[sortedIds.length - 1];
            data[this.currentRevisionKey].current = true;
        }

        const contextMenuItems = [
            {
                label: 'Open',
                data: { action: 'open' }
            },
            {
                label: this.COMPARE_CURRENT_LABEL,
                data: { action: 'compareCurrent' }
            },
            {
                label: this.COMPARE_LABEL,
                data: { action: 'compare' }
            }

        ];

        let oldProcess = null;
        let newProcess = null;

        if (this.state.showComparison) {
            const revIds = this.state.compareRevisions.sort();
            if (this.props.processRevisionsById) {
                oldProcess = this.props.processRevisionsById[revIds[0]];
            }

            if (revIds[1] === 'current') {
                newProcess = this.props.originalCopy;
            } else {
                if (this.props.processRevisionsById) {
                    newProcess = this.props.processRevisionsById[revIds[1]];
                }
            }
        }

        let isComparisonLoaded = false;
        if (newProcess && oldProcess) {
            isComparisonLoaded = true;
        }

        return (
            <Flexbox className="revision-history entity-box" flexDirection="column" padding="0 10px 10px 20px" flexGrow={1}>
                <Flexbox className={`revision-list ${this.state.showComparison ? 'short' : ''}`} flexGrow={1} minHeight={'200px'}>
                    <RevisionTable
                        process={this.props.process}
                        data={data}
                        sortedIds={sortedIds}
                        onDoubleClick={this.onDoubleClick}
                        onContextMenuItemClicked={this.onContextMenuClicked}
                        isContextMenuItemDisabled={this.isContextMenuItemDisabled}
                        contextMenuItems={contextMenuItems}
                        contextMenuId="processCatalogItemContextMenu"
                    >
                        <EntityHeader
                            title="Revision History"
                            count={sortedIds.length}
                        />
                    </RevisionTable>
                </Flexbox>
                {isComparisonLoaded ? this.renderComparisonHeader() : ''}
                {isComparisonLoaded ? this.renderCompareRevisions(oldProcess, newProcess) : ''}
            </Flexbox>
        );
    }
}

const mapStateToProps = (storeState, ownProps) => {
    const processId = ownProps.processId;
    const processState = ProcessService.getMyState(storeState);

    const processRevisionsById = processState.processRevisionsById[processId];
    const originalCopy = processState.originalCopiesById[processId];
    const processRevisions = processState.revisionHistoriesById[processId];

    return {
        processRevisions,
        processRevisionsById,
        originalCopy
    };
};


export default connect(mapStateToProps)(RevisionHistory);

class RevisionTable extends AbstractFilteredList {
    static propTypes = {
        revisions: PropTypes.array
    };

    constructor(props) {
        super(props);

        this.colWidth = {
            id: '20%',
            user: '30%',
            date: '50%'
        };
    }

    getItemClassName() {
        return 'entity revision-history';
    }

    getListClassName() {
        return 'entity-list revision-history';
    }

    renderHeaderSection() {
        const className = 'header-section';
        const headerClass = css`flex-shrink: 0;`;

        return (
            <Flexbox flexDirection="row" className={cx(className, headerClass)}>
                <Flexbox style={{ width: this.colWidth.id }}>ID</Flexbox>
                <Flexbox style={{ width: this.colWidth.user }}>User</Flexbox>
                <Flexbox style={{ width: this.colWidth.date }}>Date</Flexbox>
            </Flexbox>
        );
    }

    renderItem(key, value, selected) {
        return (
            <ListItem
                key={key}
                name={key}
                className={this.getItemClassName()}
                selected={selected}
                onClick={this.onClick}
                onDoubleClick={this.props.onDoubleClick}
            >
                <Flexbox flexDirection="row">
                    <Flexbox flexGrow={1} style={{ width: this.colWidth.id, overflow: 'hidden' }} className="list-cell">
                        {value.rev_num} { value.current ? <span style={{ marginLeft: '5px' }}><strong>[current]</strong></span> : '' }
                    </Flexbox>
                    <Flexbox flexGrow={1} style={{ width: this.colWidth.user }} className="list-cell">
                        {value.rev_user}
                    </Flexbox>
                    <Flexbox flexGrow={1} style={{ width: this.colWidth.date }} className="list-cell">
                        {value.rev_date}
                    </Flexbox>
                </Flexbox>
            </ListItem>
        );
    }

}



// WEBPACK FOOTER //
// ./src/components/page/process/RevisionHistory.js